import React, { useMemo, useState } from 'react';
import Select, { components } from 'react-select';
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPlayersForCurrentPosition,
  getStrategyCaptain,
} from '../../../../../../redux/selector/Team';
import { setTeamCaptain } from '../../../../../../redux/actions/Team';
import {
  captainPreviewLabel,
  captainChangeLabel,
  isEligibleCaptain,
} from '../../../../../../service/matchEngine';
import { simplifiedPosition } from '../../../../../../service/data';
import { fontUi } from '../../../../../../theme/typography';
import {
  selectMenuCss,
  portalSelectStyles,
} from '../../../../../../theme/selectStyles';

const STYLE_FILTERS = ['Wide', 'Central', 'Direct'];

const optionLabelCss = css`
  .captain-opt {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .captain-opt-name {
    font-weight: 600;
  }
  .captain-opt-pos {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 500;
    color: ${({ theme }) => theme.textFaint};
  }
`;

/** Same portal menu treatment as Action / Sort selects (animations included). */
const CaptainSelectMenuGlobal = createGlobalStyle`
  .captain-select__menu-portal {
    ${selectMenuCss('captain-select')}
    ${optionLabelCss}
    .captain-select__menu { min-width: max(100%, 200px); }
    .captain-select__option { font-size: 12px; min-height: 32px; padding: 6px 8px; }
    .captain-style-filters {
      display: flex;
      gap: 4px;
      padding: 2px 2px 8px;
      margin: 0 0 2px;
      border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    }
    .captain-style-filters button {
      flex: 1;
      min-width: 0;
      padding: 5px 6px;
      border: 1px solid ${({ theme }) => theme.borderSubtle};
      border-radius: ${({ theme }) => theme.controlRadius};
      background: transparent;
      color: ${({ theme }) => theme.textMuted};
      font: inherit;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
      cursor: pointer;
    }
    .captain-style-filters button:hover {
      color: ${({ theme }) => theme.text};
      background: ${({ theme }) => theme.hover};
    }
    .captain-style-filters button[aria-pressed='true'] {
      color: ${({ theme }) => theme.card};
      background: ${({ theme }) => theme.text};
      border-color: ${({ theme }) => theme.text};
    }
  }
`;

const Wrap = styled.div`
  ${fontUi};
  ${optionLabelCss}
  width: 100%;
  margin-top: 2px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.borderSubtle};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Status + change copy under the picker — wraps, no name repeat. */
const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  min-width: 0;
`;

const Status = styled.p`
  margin: 0;
  flex: 1 1 10rem;
  min-width: 0;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.textMuted};
`;

const ChangeNote = styled.p`
  margin: 0;
  flex: 1 1 8rem;
  min-width: 0;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.textFaint};
`;

const AutoBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.controlRadius};
  background: ${({ theme }) => theme.headerAccent || '#00bd83'};
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.2;
`;

/** Matches Transfer Action / Invite selects — card chrome, not a grey fill. */
const CaptainSelect = styled(Select)`
  width: 100%;
  ${fontUi};
  font-size: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.controlRadius};
  background: ${({ theme }) => theme.card};
  .captain-select__control {
    min-height: 34px;
    border: 0;
    background: transparent;
    box-shadow: none;
    cursor: pointer;
    color: ${({ theme }) => theme.text};
  }
  .captain-select__control--is-focused {
    outline: 2px solid ${({ theme }) => theme.headerAccent};
    outline-offset: 2px;
  }
  .captain-select__control--menu-is-open .captain-select__dropdown-indicator {
    transform: rotate(180deg);
  }
  .captain-select__value-container {
    padding: 0 8px;
  }
  .captain-select__single-value,
  .captain-select__input-container {
    color: ${({ theme }) => theme.text};
    font-weight: 600;
    font-size: 12px;
    margin: 0;
  }
  .captain-select__placeholder {
    color: ${({ theme }) => theme.textFaint};
    font-weight: 500;
    font-size: 12px;
    margin: 0;
  }
  .captain-select__indicator {
    padding: 2px 8px;
    transition: transform 160ms ease-out;
    color: ${({ theme }) => theme.textFaint};
  }
  .captain-select__indicator svg {
    width: 12px;
    fill: ${({ theme }) => theme.textFaint};
  }
  .captain-select__indicator-separator {
    display: none;
  }
`;

const posLabel = (pos) => simplifiedPosition[pos] || pos || '';

function CaptainStyleFilters({ styleFilter, onStyleFilter }) {
  return (
    <div
      className="captain-style-filters"
      role="group"
      aria-label="Filter captains by style"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      {STYLE_FILTERS.map((style) => (
        <button
          key={style}
          type="button"
          aria-pressed={styleFilter === style}
          onClick={() =>
            onStyleFilter(styleFilter === style ? null : style)
          }
        >
          {style}
        </button>
      ))}
    </div>
  );
}

export default function CaptainPanel() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const captain = useSelector(getStrategyCaptain);
  const players = useSelector(getPlayersForCurrentPosition) || [];
  const [busy, setBusy] = useState(false);
  const [styleFilter, setStyleFilter] = useState(null);

  const options = useMemo(
    () =>
      players
        .filter(isEligibleCaptain)
        .slice()
        .sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`
          )
        )
        .map((p) => ({
          value: p._id,
          label: `${p.first_name} ${p.last_name}`,
          position: posLabel(p.fav_position),
          style: p.fav_style || null,
        })),
    [players]
  );

  const filteredOptions = useMemo(() => {
    if (!styleFilter) return options;
    return options.filter((option) => option.style === styleFilter);
  }, [options, styleFilter]);

  const menuList = useMemo(
    () =>
      function CaptainMenuList(props) {
        return (
          <components.MenuList {...props}>
            <CaptainStyleFilters
              styleFilter={styleFilter}
              onStyleFilter={setStyleFilter}
            />
            {props.children}
          </components.MenuList>
        );
      },
    [styleFilter]
  );

  const change = captain?.change;
  const canChange = !!change?.allowed;
  const previewText = captainPreviewLabel(captain?.preview);
  const changeText = captainChangeLabel(change, captain?.seasonChangesLeft);
  const currentId = captain?.player?._id;
  const selected = options.find((o) => o.value === currentId) || null;
  const statusText =
    previewText || (selected ? null : 'No captain selected');

  const onChange = async (option) => {
    if (!option?.value || !canChange || busy) return;
    if (currentId && String(option.value) === String(currentId)) return;
    setBusy(true);
    await dispatch(setTeamCaptain(option.value));
    setBusy(false);
  };

  const formatOptionLabel = (option) => (
    <span className="captain-opt">
      <span className="captain-opt-name">{option.label}</span>
      {option.position ? (
        <span className="captain-opt-pos">{option.position}</span>
      ) : null}
      {option.style ? (
        <span className="captain-opt-pos">{option.style}</span>
      ) : null}
    </span>
  );

  return (
    <Wrap aria-label="Squad captain">
      <CaptainSelectMenuGlobal />
      <CaptainSelect
        aria-label="Captain"
        classNamePrefix="captain-select"
        inputId="strategy-Captain"
        isSearchable={false}
        isDisabled={!canChange || busy}
        options={filteredOptions}
        value={selected}
        placeholder="Select outfield player…"
        onChange={onChange}
        onMenuClose={() => setStyleFilter(null)}
        components={{ MenuList: menuList }}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() =>
          styleFilter ? `No ${styleFilter} captains` : 'No eligible captains'
        }
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        styles={portalSelectStyles(theme, 70)}
      />
      <Meta>
        {statusText ? <Status>{statusText}</Status> : null}
        {changeText ? <ChangeNote>{changeText}</ChangeNote> : null}
        {captain?.auto ? (
          <AutoBadge title="Chosen for you each match. Pick your own captain any time, free.">
            Auto
          </AutoBadge>
        ) : null}
      </Meta>
    </Wrap>
  );
}
